import { useState, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import BannerImage from '../../components/banner/banner-image'
import SmallCard from '../../components/cards/small-card'
import LazyImage from '../../components/lazy-image'
import ContentContainer from '../../areas/layout/content-container'
import moviesApi from '../../client/movies-api'
import showsApi from '../../client/shows-api'
import featuredApi from '../../client/featured-api'
import { MovieDTO } from '../../types/Movie'
import { ShowDTO } from '../../types/Show'
import { FeaturedItemDTO } from '../../types/Featured'
import TrailerModal from '../../components/trailer-modal/trailer-modal'
import GuideBanner from '../../components/guide-banner'
import MoodPicker from '../../components/mood-picker/mood-picker'
import EmptyState from '../../utils/empty-state'
import {
  StyledArrowButton,
  StyledBannerContent,
  StyledBannerInfo,
  StyledBottomSection,
  StyledButtonWrapper,
  StyledCardScroller,
  StyledDot,
  StyledDotsWrapper,
  StyledFeaturedLabel,
  StyledGenreChip,
  StyledGenreWrapper,
  StyledLoadingWrapper,
  StyledMetaText,
  StyledMetaWrapper,
  StyledOverview,
  StyledPageWrapper,
  StyledPoster,
  StyledPosterFallback,
  StyledPrimaryButton,
  StyledRating,
  StyledSectionHeader,
  StyledSectionTitle,
  StyledSectionWrapper,
  StyledSlide,
  StyledSliderContainer,
  StyledTitle,
  StyledViewAllButton,
} from './home-page-styles'
import { useMobileBreakpoints } from '../../utils/mobile-breakpoints'

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [featured, setFeatured] = useState<FeaturedItemDTO[]>([])
  const [trendingMovies, setTrendingMovies] = useState<MovieDTO[]>([])
  const [popularMovies, setPopularMovies] = useState<MovieDTO[]>([])
  const [trendingShows, setTrendingShows] = useState<ShowDTO[]>([])
  const [popularShows, setPopularShows] = useState<ShowDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [trailerOpen, setTrailerOpen] = useState(false)
  const [selectedTrailerUrl, setSelectedTrailerUrl] = useState<string | null>(
    null,
  )
  const [selectedTrailerTitle, setSelectedTrailerTitle] = useState('')

  const { isMobileMD } = useMobileBreakpoints()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          featuredRes,
          trendingMovieRes,
          popularMovieRes,
          trendingShowRes,
          popularShowRes,
        ] = await Promise.all([
          featuredApi.getFeatured(20),
          moviesApi.getTrending(1),
          moviesApi.getPopular(1),
          showsApi.getTrending(1),
          showsApi.getPopular(1),
        ])

        setFeatured(featuredRes.data)
        setTrendingMovies(trendingMovieRes.data.results)
        setPopularMovies(popularMovieRes.data.results)
        setTrendingShows(trendingShowRes.data.results)
        setPopularShows(popularShowRes.data.results)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (featured.length === 0 || trailerOpen) return

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featured.length)
    }, 8000)
    return () => clearInterval(interval)
  }, [featured.length, trailerOpen])

  const handleWatchTrailer = (title: string, trailerUrl?: string) => {
    if (trailerUrl) {
      setSelectedTrailerTitle(title)
      setSelectedTrailerUrl(trailerUrl)
      setTrailerOpen(true)
    }
  }

  if (loading) {
    return (
      <StyledLoadingWrapper>
        <CircularProgress />
      </StyledLoadingWrapper>
    )
  }

  if (featured.length === 0) {
    return (
      <StyledLoadingWrapper>
        <EmptyState
          title='No content to show right now'
          description='We could not load any featured titles. Please check back soon.'
        />
      </StyledLoadingWrapper>
    )
  }

  return (
    <StyledPageWrapper>
      <StyledSliderContainer>
        {featured.map((item, index) => (
          <StyledSlide
            key={`${item.mediaType}-${item.id}`}
            isActive={index === currentSlide}
          >
            <BannerImage
              imageUrl={item.backdropUrl}
              height={isMobileMD ? 800 : 550}
            >
              <ContentContainer width={isMobileMD ? 100 : 80}>
                <StyledBannerContent>
                  <StyledPoster>
                    <LazyImage
                      src={item.posterUrl}
                      alt={item.title}
                      fallback={
                        <StyledPosterFallback>
                          {item.title.slice(0, 1).toUpperCase() || '?'}
                        </StyledPosterFallback>
                      }
                    />
                  </StyledPoster>

                  <StyledBannerInfo>
                    <StyledFeaturedLabel>Featured</StyledFeaturedLabel>
                    <StyledTitle>{item.title}</StyledTitle>

                    <StyledMetaWrapper>
                      {item.rating ? (
                        <StyledRating value={item.rating}>
                          <StarRoundedIcon fontSize='inherit' />
                          {item.rating}
                        </StyledRating>
                      ) : null}
                      <StyledMetaText>{item.year}</StyledMetaText>
                    </StyledMetaWrapper>

                    <StyledGenreWrapper>
                      {item.genres.slice(0, 3).map((genre) => (
                        <StyledGenreChip key={genre}>{genre}</StyledGenreChip>
                      ))}
                    </StyledGenreWrapper>

                    <StyledOverview>{item.overview}</StyledOverview>

                    <StyledButtonWrapper>
                      <StyledPrimaryButton
                        startIcon={<PlayArrowIcon />}
                        onClick={() =>
                          handleWatchTrailer(item.title, item.trailerUrl)
                        }
                        disabled={!item.trailerUrl}
                      >
                        Watch Trailer
                      </StyledPrimaryButton>
                      <StyledArrowButton
                        component={RouterLink}
                        to={`/details/${item.mediaType === 'tv' ? 'show' : 'movie'}/${item.id}`}
                      >
                        <ArrowForwardIcon />
                      </StyledArrowButton>
                    </StyledButtonWrapper>
                  </StyledBannerInfo>
                </StyledBannerContent>
              </ContentContainer>
            </BannerImage>
          </StyledSlide>
        ))}

        <StyledDotsWrapper>
          {featured.map((_, index) => (
            <StyledDot
              key={index}
              active={index === currentSlide}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </StyledDotsWrapper>
      </StyledSliderContainer>

      <GuideBanner />

      <ContentContainer>
        <MoodPicker />

        <StyledSectionWrapper>
          <StyledSectionHeader>
            <StyledSectionTitle variant='h5'>
              Trending Movies
            </StyledSectionTitle>
            <StyledViewAllButton component={RouterLink} to='/movies/trending'>
              View all {'>'}
            </StyledViewAllButton>
          </StyledSectionHeader>
          <StyledCardScroller>
            {trendingMovies.map((movie) => (
              <SmallCard
                key={movie.id}
                id={movie.id}
                type='movie'
                title={movie.title}
                year={movie.year || ''}
                rating={movie.rating}
                posterUrl={movie.posterUrl}
                genres={movie.genres}
              />
            ))}
          </StyledCardScroller>
        </StyledSectionWrapper>

        <StyledSectionWrapper>
          <StyledSectionHeader>
            <StyledSectionTitle variant='h5'>Trending Shows</StyledSectionTitle>
            <StyledViewAllButton component={RouterLink} to='/shows/trending'>
              View all {'>'}
            </StyledViewAllButton>
          </StyledSectionHeader>
          <StyledCardScroller>
            {trendingShows.map((show) => (
              <SmallCard
                key={show.id}
                id={show.id}
                type='show'
                title={show.title}
                year={show.year || ''}
                rating={show.rating}
                posterUrl={show.posterUrl}
                genres={show.genres}
              />
            ))}
          </StyledCardScroller>
        </StyledSectionWrapper>

        <StyledBottomSection>
          <StyledSectionHeader>
            <StyledSectionTitle variant='h5'>Popular Movies</StyledSectionTitle>
            <StyledViewAllButton component={RouterLink} to='/movies/popular'>
              View all {'>'}
            </StyledViewAllButton>
          </StyledSectionHeader>
          <StyledCardScroller>
            {popularMovies.map((movie) => (
              <SmallCard
                key={movie.id}
                id={movie.id}
                type='movie'
                title={movie.title}
                year={movie.year || ''}
                rating={movie.rating}
                posterUrl={movie.posterUrl}
                genres={movie.genres}
              />
            ))}
          </StyledCardScroller>
        </StyledBottomSection>

        <StyledBottomSection>
          <StyledSectionHeader>
            <StyledSectionTitle variant='h5'>Popular Shows</StyledSectionTitle>
            <StyledViewAllButton component={RouterLink} to='/shows/popular'>
              View all {'>'}
            </StyledViewAllButton>
          </StyledSectionHeader>
          <StyledCardScroller>
            {popularShows.map((show) => (
              <SmallCard
                key={show.id}
                id={show.id}
                type='show'
                title={show.title}
                year={show.year || ''}
                rating={show.rating}
                posterUrl={show.posterUrl}
                genres={show.genres}
              />
            ))}
          </StyledCardScroller>
        </StyledBottomSection>
      </ContentContainer>

      <TrailerModal
        open={trailerOpen}
        onClose={() => setTrailerOpen(false)}
        title={selectedTrailerTitle}
        trailerUrl={selectedTrailerUrl}
      />
    </StyledPageWrapper>
  )
}

export default HomePage
